import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Box, Tooltip } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SCUIContent } from '../../atomic/organisms';
import { SCUICustomDataGrid, SCUIGeneralDrawer, SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIButton } from '../../atomic/atoms';
import { IpWhiteListQuery } from '../../services/query';
import { IpWhiteListCommand } from '../../services/command';
import { getColumns, initialState } from './IpWhiteList.helper';
import { IpWhiteListForm } from './components/IpWhiteListForm';
import Swal from 'sweetalert2';
import { SCUILoading } from '../../atomic/atoms';
import { IPWL, iwlCls } from './ipWhiteList.classes';

export const IpWhiteList: FC = () => {
    const { t } = useTranslation();
    const [state, setState] = useState(initialState);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setState((prev) => ({ ...prev, loading: true }));
        try {
            const response = await IpWhiteListQuery.GetIpWhiteListAll();
            setState((prev) => ({
                ...prev,
                ipWhiteListData: response?.data?.items || [],
                loading: false,
            }));
        } catch (error) {
            setState((prev) => ({ ...prev, loading: false }));
            console.error('Error loading IP whitelist data:', error);
        }
    };

    const handleAdd = () => {
        setState((prev) => ({
            ...prev,
            drawerOpen: true,
            selectedRow: null,
            isEditMode: false,
        }));
    };

    const handleEdit = (row: any) => {
        setState((prev) => ({
            ...prev,
            drawerOpen: true,
            selectedRow: row,
            isEditMode: true,
        }));
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: t('general.sureText'),
            text: t('ipWhiteList.deleteConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        });

        if (result.isConfirmed) {
            try {
                const username = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username || 'System';
                await IpWhiteListCommand.DeleteIpWhiteList(id, {
                    id: id,
                    updatedBy: username,
                });
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('ipWhiteList.deleteSuccess'),
                    timer: 2000,
                });
                loadData();
            } catch {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('general.couldntDeleted'),
                });
            }
        }
    };

    const handleSave = async (formData: any) => {
        try {
            const username = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username || 'System';

            if (state.isEditMode) {
                const updateData = {
                    id: formData.id,
                    ipAddress: formData.ipAddress,
                    description: formData.description,
                    updatedBy: username,
                };
                await IpWhiteListCommand.UpdateIpWhiteList(formData.id, updateData);
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('ipWhiteList.updateSuccess'),
                    timer: 2000,
                });
            } else {
                const addData = {
                    ipAddress: formData.ipAddress,
                    description: formData.description,
                    createdBy: username,
                };
                await IpWhiteListCommand.AddIpWhiteList(addData);
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('ipWhiteList.addSuccess'),
                    timer: 2000,
                });
            }
            handleCloseDrawer();
            loadData();
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: state.isEditMode ? t('general.couldntUpdated') : t('general.couldntAdded'),
            });
        }
    };

    const handleCloseDrawer = () => {
        setState((prev) => ({
            ...prev,
            drawerOpen: false,
            selectedRow: null,
            isEditMode: false,
        }));
    };

    const breadcrumbList = [{ label: t('general.home'), path: '/home' }, { label: t('ipWhiteList.title') }];

    const baseColumns = getColumns(t);

    const actionsColumn = {
        field: 'actions',
        headerName: t('ipWhiteList.actions'),
        flex: 0.8,
        minWidth: 100,
        sortable: false,
        renderCell: (params: any) => (
            <Box className={IPWL.actionsCellWrap} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Tooltip title={t('general.edit')} arrow>
                    <Button
                        className={IPWL.inputButtonRowEdit}
                        onClick={() => handleEdit(params.row)}
                        sx={{
                            minWidth: '30px !important',
                            width: '30px !important',
                            minHeight: '30px !important',
                            height: '30px !important',
                            backgroundColor: '#00B8F4',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'none',
                            p: 0,
                            m: 0,
                            '&:hover': {
                                backgroundColor: '#0099c8',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <EditIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                </Tooltip>
                <Tooltip title={t('general.delete')} arrow>
                    <Button
                        className={IPWL.inputButtonRowDelete}
                        onClick={() => handleDelete(params.row.id)}
                        sx={{
                            minWidth: '30px !important',
                            width: '30px !important',
                            minHeight: '30px !important',
                            height: '30px !important',
                            backgroundColor: '#dc3545',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'none',
                            p: 0,
                            m: 0,
                            '&:hover': {
                                backgroundColor: '#bb2d3b',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <DeleteIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                </Tooltip>
            </Box>
        ),
    };

    // Merge columns with actions
    const columns = [...baseColumns, actionsColumn];

    return (
        <SCUIContent>
            <Box className={IPWL.page}>
            <div className={IPWL.breadcrumbSection}>
                <SCUIBreadCrumb data={breadcrumbList} />
            </div>

            <Box
                className={IPWL.headerToolbar}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '24px',
                    marginBottom: '24px',
                }}
            >
                <h2 className={iwlCls('headtxt', IPWL.pageTitle)}>{t('ipWhiteList.title')}</h2>
                <SCUIButton
                    className={IPWL.inputButtonAddIp}
                    text={t('ipWhiteList.addNewIp')}
                    type="primary"
                    sizeType="fixed"
                    style={{ height: '40px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={handleAdd}
                    showStartIcon
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                />
            </Box>

            {state.loading ? (
                <Box className={IPWL.loadingWrap}>
                    <SCUILoading />
                </Box>
            ) : (
                <Box className={IPWL.gridWrap} sx={{ height: 600, width: '100%' }}>
                    <SCUICustomDataGrid
                        rows={state.ipWhiteListData}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        pagination
                        paginationMode="client"
                        disableSelectionOnClick
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 10 },
                            },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #e0e0e0',
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f5f5f5',
                                borderBottom: '2px solid #e0e0e0',
                            },
                        }}
                    />
                </Box>
            )}

            <SCUIGeneralDrawer open={state.drawerOpen} onClose={handleCloseDrawer} anchor="right">
                <IpWhiteListForm data={state.selectedRow} onSave={handleSave} onClose={handleCloseDrawer} />
            </SCUIGeneralDrawer>
            </Box>
        </SCUIContent>
    );
};
